import React, {useEffect} from 'react';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { faBars } from '@fortawesome/pro-light-svg-icons/faBars';

import FA from 'utils/FA';
import duckify, { singleDuck } from '../../utils/duckify';
import { sharedPortalUsers, sharedPortalSchoolGroups } from '../../ducks';
import SubPageNavigation from '../SubPageNavigation';
import FlashRoot from '../FlashMessage/FlashRoot';
import PersonIcon from './PersonIcon';
import logo from '../../../../images/shared_components/fsems_logo.svg';
import useWindowSize from '../../hooks/useWindowSize';
import { isBrandedPortal } from './utils/schools';

import './styles/Layout.scss';

const HELP_CENTER_LINK = ['Help Center', 'https://www.finalsite.com/explore-help-center'];
const GTM_CONTAINER_ID = "GTM-MCQWNCZC"

/**
 * The main layout component for the shared portal. Page components like `<StudentIndex/>` are injected
 * as a child component via nested react routes.
 */
const Layout = ({ children, user, currentGroup, location, duckMeta }) => {
  const { isMobile } = useWindowSize();
  const isIntake = location.pathname.startsWith("/intake");

  useEffect(() => {
    if (GTM_CONTAINER_ID && !document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}"]`)) {
        const gtmScript = document.createElement('script')
        gtmScript.id = 'gtm-script'
        gtmScript.async = true
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`
        document.head.appendChild(gtmScript)
      }

    if (currentGroup && currentGroup.google_analytics_4_code && !document.getElementById('ga4-script')) {
        const ga4Script = document.createElement('script')
        ga4Script.id ='ga4-script'
        ga4Script.async = true
        ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${currentGroup.google_analytics_4_code}`
        document.head.appendChild(ga4Script)

        const ga4ConfigScript = document.createElement('script')
        ga4ConfigScript.id = 'ga4-config-script'
        ga4ConfigScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${currentGroup.google_analytics_4_code}');
        `
        document.head.appendChild(ga4ConfigScript)
      }

      return () => {
        const gtmScript = document.getElementById('gtm-script')
        if(gtmScript) gtmScript.remove();
        const ga4Script = document.getElementById('ga4-script')
        if(ga4Script) ga4Script.remove();
        const ga4ConfigScript = document.getElementById('ga4-config-script')
        if(ga4ConfigScript) ga4ConfigScript.remove();
      }
  }, [currentGroup])

  const generateLinks = () => {
    if (isMobile()) {
      return [
        [
          <div className='sp-hamburger fa-layers fa-fw'>
            <FA icon={faBars} className='fa-2x' />
          </div>,
          '',
          [
            ['Dashboard', '/'],
            ['Explore Schools', '/explore_schools'],
            HELP_CENTER_LINK
          ]
        ]
      ];
    }

    return [['Dashboard', '/'], ['Explore Schools', '/explore_schools']];
  }

  const myProfileLink = isIntake ? [] : [['My Profile', '/profile']];

  const loggedIn = Object.keys(user).length !== 0;
  const isEmbed = location.search.includes('embed=true');

  const profileLinks = loggedIn ? [
    [
      <PersonIcon personName={`${user.first_name} ${user.last_name}`} sameLine />,
      '/profile',
      [...myProfileLink, ['Log Out', '/shared_portal_users/sign_out']]
    ]
  ] : [['Log In', '/shared_portal_users/sign_in', [], isEmbed]];

  if (currentGroup && currentGroup.custom_background_url && !isEmbed) {
    document.body.style.backgroundImage = `url('${currentGroup.custom_background_url}')`;
  }

  if (isEmbed) {
    document.body.style.backgroundImage = 'none';
  }

  let customOrDefaultLogo = '';
  if (duckMeta.currentGroup.ready && isBrandedPortal(currentGroup, window.location.hostname)) {
    customOrDefaultLogo = currentGroup.custom_logo_url ? currentGroup.custom_logo_url : logo;
  } else {
    customOrDefaultLogo = duckMeta.currentGroup.ready ? logo : "";
  }

  return (
    <React.Fragment>
      {!isEmbed && (
        <div className="sp-header-top">
          <div className="header-brand">
            <a href="/">
              <img src={customOrDefaultLogo} alt="Finalsite Enrollment" />
            </a>
          </div>
        </div>
      )}

      <div className="sp-layout-container">
        <div className="sp-layout-header sp-container">
          {!isIntake && loggedIn && (<SubPageNavigation links={generateLinks()} listClassName='sp-header-list' />)}
          {(isIntake || !loggedIn) && isMobile() && (
            <SubPageNavigation
              links={
                [[
                  <div className='sp-hamburger fa-layers fa-fw'>
                    <FA icon={faBars} className='fa-2x' />
                  </div>,
                  '',
                  [HELP_CENTER_LINK]
                ]]
              }
              listClassName='sp-header-list'
            />
          )}
          <div className="sp-layout-header-interspace"></div>
          {!isMobile() && (
            <SubPageNavigation
              links={[HELP_CENTER_LINK]}
              listClassName='sp-header-list'
            />
          )}
          <SubPageNavigation links={profileLinks} forceAnchor={!loggedIn} listClassName='sp-header-list' />
        </div>
        <FlashRoot/>
        <div className="sp-layout-main sp-container">
          {children}
        </div>
      </div>
    </React.Fragment>
  );
};

const mapDispatchToProps = (_dispatch) => ({});

const mapDucksToProps = (_props) => ({
  user: singleDuck(sharedPortalUsers, 'current'),
  currentGroup: singleDuck(sharedPortalSchoolGroups, 'current')
});

export default compose(
  withRouter,
  duckify(mapDucksToProps, undefined, mapDispatchToProps)
)(Layout);
